/**
 * Синхронизация JSON-состояния workspace через Y.Map('workspace')
 * (last-write-wins). Ключ dataKey — чтобы на одной сессии урока
 * разные задачи не делили один черновик.
 */
/* eslint-disable react-hooks/refs */
import { useCallback, useEffect, useRef, useState } from 'react';

const Y_KEY = 'workspace';

export function emptyGraphTableState(mode = 'graph') {
  return {
    version: 1,
    mode: mode === 'tree' ? 'tree' : 'graph',
    nodes: [],
    edges: [],
    rowLabels: [],
    colLabels: [],
    cells: {},
  };
}

export function emptySpreadsheetState(rows = 8, cols = 6) {
  const cells = {};
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      cells[`${r},${c}`] = '';
    }
  }
  return {
    version: 1,
    rows,
    cols,
    cells,
  };
}

function normalizeState(raw, fallback) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return fallback;
  return { ...fallback, ...raw };
}

/**
 * @param {object} opts
 * @param {import('yjs').Doc | null} opts.ydoc
 * @param {object} opts.initialState
 * @param {object | null} [opts.seedState]
 * @param {string} [opts.dataKey]
 * @param {(state: object) => void} [opts.onChange]
 */
export function useWorkspaceSync({ ydoc, initialState, seedState = null, dataKey = 'data', onChange }) {
  const fallbackRef = useRef(initialState);
  const [state, setState] = useState(() =>
    normalizeState(seedState || initialState, initialState),
  );
  const applyingRemote = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    fallbackRef.current = initialState;
  }, [initialState]);

  useEffect(() => {
    if (!ydoc) return undefined;
    const ymap = ydoc.getMap(Y_KEY);
    const key = String(dataKey || 'data');

    const read = () => {
      const raw = ymap.get(key);
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        return normalizeState(raw, fallbackRef.current);
      }
      return null;
    };

    const existing = read();
    if (!existing) {
      const seed = normalizeState(seedState || initialState, initialState);
      ydoc.transact(() => {
        ymap.set(key, seed);
      });
      applyingRemote.current = true;
      setState(seed);
      applyingRemote.current = false;
    } else {
      applyingRemote.current = true;
      setState(existing);
      applyingRemote.current = false;
    }

    const observer = () => {
      const next = read();
      if (!next) return;
      applyingRemote.current = true;
      setState(next);
      applyingRemote.current = false;
      if (typeof onChangeRef.current === 'function') onChangeRef.current(next);
    };
    ymap.observe(observer);
    return () => ymap.unobserve(observer);
  }, [ydoc, initialState, seedState, dataKey]);

  const replaceState = useCallback(
    (nextOrFn) => {
      setState((prev) => {
        const next = typeof nextOrFn === 'function' ? nextOrFn(prev) : nextOrFn;
        const normalized = normalizeState(next, fallbackRef.current);
        if (ydoc && !applyingRemote.current) {
          const ymap = ydoc.getMap(Y_KEY);
          const key = String(dataKey || 'data');
          ydoc.transact(() => {
            ymap.set(key, normalized);
          });
        }
        if (typeof onChangeRef.current === 'function') onChangeRef.current(normalized);
        return normalized;
      });
    },
    [ydoc, dataKey],
  );

  return [state, replaceState];
}
